import { alpha } from '@mui/material/styles';
import type { ChartData, ChartOptions, ChartTypeRegistry } from 'chart.js';

import type { Colors } from 'src/theme/paletteColors';
import type { DashboardWidgetProps } from 'src/types/dashboard';
import type { ChartConfig } from 'src/services/chartDataTransformation';
import { ChartType, DimensionType } from 'src/services/chartDataTransformation';

// Helper to merge plugin configurations from multiple sources
// Each plugin config object (e.g., legend, title) is merged individually
export const mergePlugins = (
  ...pluginObjects: Array<Record<string, any> | undefined>
): Record<string, any> => {
  const result: Record<string, any> = {};

  pluginObjects.forEach((plugins) => {
    if (!plugins) return;

    Object.keys(plugins).forEach((pluginName) => {
      result[pluginName] = {
        ...result[pluginName],
        ...plugins[pluginName],
      };
    });
  });

  return result;
};

export const DEFAULT_CHART_OPTIONS: ChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
    },
  },
};

export const FALLBACK_CHART_OPTIONS: ChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
};

export const createDefaultPalette = (colors: Colors): string[] =>
  [
    colors.generalVeryPositive,
    colors.generalPositive,
    colors.generalNeutral,
    colors.generalNegative,
    colors.generalVeryNegative,
    colors.primary[500],
    colors.secondary[500],
    colors.tertiary[500],
    colors.success[500],
    colors.warning[500],
    colors.info[500],
    colors.error[500],
  ].filter((color): color is string => !!color);

export const applySeriesOverrides = (
  chartData: ChartData,
  chartConfig: ChartConfig,
  seriesConfig: DashboardWidgetProps['config']['data_series_config'],
): ChartData => {
  if (!chartData?.datasets?.length || !seriesConfig) {
    return chartData;
  }

  const statisticKeys =
    chartConfig.seriesData === DimensionType.STATISTICS
      ? chartConfig.selections.statistics
      : undefined;

  chartData.datasets = chartData.datasets.map((dataset, index) => {
    const potentialKeys = [
      statisticKeys?.[index],
      typeof dataset.label === 'string' ? dataset.label : undefined,
      typeof dataset.label === 'string'
        ? dataset.label.replace(/\s*\(.*\)$/, '')
        : undefined,
    ].filter((key): key is string => Boolean(key));

    const overrideKey = potentialKeys.find((key) => seriesConfig[key]);
    if (!overrideKey) {
      return dataset;
    }

    return {
      ...dataset,
      ...seriesConfig[overrideKey],
    };
  }) as typeof chartData.datasets;

  return chartData;
};

export const applyDefaultColors = (
  chartData: ChartData,
  chartType: string,
  palette: string[],
): ChartData => {
  if (!chartData?.datasets?.length || palette.length === 0) {
    return chartData;
  }

  const lineLikeTypes = new Set(['line']);

  chartData.datasets = chartData.datasets.map((dataset, index) => {
    const color = palette[index % palette.length];

    if (!color) {
      return dataset;
    }

    if (lineLikeTypes.has(chartType)) {
      const lineDataset =
        dataset as ChartTypeRegistry['line']['datasetOptions'];
      return {
        ...lineDataset,
        borderColor: lineDataset.borderColor ?? color,
        backgroundColor: lineDataset.backgroundColor ?? alpha(color, 0.2),
        pointBackgroundColor: lineDataset.pointBackgroundColor ?? color,
        pointBorderColor: lineDataset.pointBorderColor ?? color,
      };
    }

    return {
      ...dataset,
      backgroundColor: dataset.backgroundColor ?? color,
      borderColor: dataset.borderColor ?? color,
      hoverBackgroundColor: dataset.hoverBackgroundColor ?? alpha(color, 0.85),
    };
  }) as typeof chartData.datasets;

  return chartData;
};

// Normalize widget config so downstream code always receives a ChartConfig.
// "Normalize" here means: accept the admin's saved payload regardless of where the
// ChartConfig lives, pass through existing ChartConfig objects, unwrap ones nested
// under `config.chartconfig`, or, while the Django admin still emits the legacy
// fields (`questions`, `statistics`, `comparison_type`), assemble the equivalent
// ChartConfig that the transformation service expects.
export const normalizeChartConfig = (config: any): ChartConfig | null => {
  const includeAncestors = config.comparison_type === 1;

  // Check if chartconfig contains the new dimension mapping format
  if (config.chartconfig?.xAxisData && config.chartconfig?.seriesData) {
    // New format stored in chartconfig
    const { xAxisData, seriesData, ...restConfig } = config.chartconfig;

    return {
      chartType: restConfig.type === 'line' ? ChartType.LINE : ChartType.BAR,
      xAxisData: xAxisData as DimensionType,
      seriesData: seriesData as DimensionType,
      selections: {
        questions: config.questions || [],
        statistics: config.statistics || [],
        datalines: {
          focal: true,
          children: config.comparison_type === 2 ? 'all' : 'none',
          ancestors: includeAncestors,
          selected: 'none',
        },
      },
      labels: {
        statisticLabels: config.series_labels,
        datalineLabels: restConfig.datalineLabels,
      },
      dataHandling: restConfig.dataHandling,
      sorting: restConfig.sorting,
    } as ChartConfig;
  }

  // Check if this is already in the new format at the top level
  if (
    config.chartType &&
    config.xAxisData &&
    config.seriesData &&
    config.selections
  ) {
    return config as ChartConfig;
  }

  // Legacy format detection and mapping
  // This is a basic mapping - extend as needed based on actual legacy configs
  const chartType = config.chartconfig?.type || 'bar';
  const questions = config.questions || [];
  const statistics = config.statistics || [];

  if (questions.length === 0 || statistics.length === 0) {
    return null;
  }

  // Default mapping: questions on x-axis, statistics as series
  return {
    chartType: chartType === 'line' ? ChartType.LINE : ChartType.BAR,
    xAxisData: DimensionType.QUESTIONS,
    seriesData: DimensionType.STATISTICS,
    selections: {
      questions,
      statistics,
      datalines: {
        focal: true,
        children: config.comparison_type === 2 ? 'all' : 'none',
        ancestors: includeAncestors,
        selected: 'none',
      },
    },
    labels: {
      statisticLabels: config.series_labels,
    },
  };
};
